import { useState, useEffect } from 'react';
import { collection, query, limit, getDocs, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { openSupportChatAndGo } from '../utils/supportChat';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const [usersSnap, driversSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), limit(100))),
          getDocs(query(collection(db, 'drivers'), limit(100)))
        ]);

        const usersMap = new Map();

        usersSnap.forEach(doc => {
          usersMap.set(doc.id, { 
            ...doc.data(), 
            id: doc.id, 
            isPassenger: true, 
            isDriver: false,
            isPhoneVerified: doc.data().phoneVerified === true
          });
        });

        driversSnap.forEach(doc => {
          if (usersMap.has(doc.id)) {
            const existing = usersMap.get(doc.id);
            existing.isDriver = true;
            existing.driverData = doc.data();
            if (!existing.name && !existing.firstName) {
               existing.name = doc.data().name;
            }
            existing.isPhoneVerified = existing.isPhoneVerified || doc.data().phoneVerified === true;
          } else {
            usersMap.set(doc.id, { 
              ...doc.data(), 
              id: doc.id, 
              isPassenger: false, 
              isDriver: true,
              driverData: doc.data(),
              isPhoneVerified: doc.data().phoneVerified === true
            });
          }
        });

        const combined = Array.from(usersMap.values());
        combined.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return dateB - dateA;
        });

        setUsers(combined);
      } catch (error) {
        console.error("Error fetching all users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, []);

  const handleDelete = async (targetUid) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar a este usuario? Esta acción es irreversible y borrará todo su rastro.")) return;

    try {
      const functions = getFunctions();
      const deleteUserCallable = httpsCallable(functions, 'deleteUser');
      await deleteUserCallable({ targetUid });
      setUsers(users.filter(u => u.id !== targetUid));
      alert("Usuario eliminado exitosamente.");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Error al eliminar usuario: " + error.message);
    }
  };

  if (loading) {
    return <div className="skeleton-card" style={{height:'400px'}} />;
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h2 className="page-title">Usuarios Globales</h2>
          <p className="page-subtitle">Lista unificada de todos los pasajeros y conductores</p>
        </div>
        <div style={{
          background: 'rgba(6, 182, 212, 0.08)',
          border: '1px solid rgba(6, 182, 212, 0.2)',
          borderRadius: 'var(--radius-md)',
          padding: '0.5rem 1rem',
          fontSize: '0.85rem',
          color: 'var(--accent-cyan)',
          fontWeight: 600
        }}>
          {users.length} usuarios
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Rol</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>Viajes</th>
                <th>Calificación</th>
                <th>Registro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? users.map((user) => (
                <tr key={user.id}>
                  <td className="mono-text">{user.id.slice(0,8)}...</td>
                  <td>
                    {user.isPassenger && user.isDriver && (
                      <span className="badge" style={{background: 'var(--accent-purple)', color: '#fff'}}>Ambos</span>
                    )}
                    {user.isPassenger && !user.isDriver && (
                      <span className="badge" style={{background: 'var(--accent-cyan)', color: '#fff'}}>Pasajero</span>
                    )}
                    {!user.isPassenger && user.isDriver && (
                      <span className="badge" style={{background: 'var(--accent-orange)', color: '#fff'}}>Conductor</span>
                    )}
                  </td>
                  <td className="font-medium">
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      {user.firstName || user.name || ''} {user.lastName || ''}
                      {(user.certified === true || user.driverData?.certified === true) && (
                        <span 
                          title="Conductor Certificado"
                          style={{
                            background: '#10b981', 
                            color: 'white', 
                            fontSize: '0.65rem', 
                            padding: '2px 6px', 
                            borderRadius: '10px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                          }}>
                          CERTIFICADO
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-sm">{user.email}</td>
                  <td className="text-sm">
                    {user.phone ? (
                      <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                        {user.phone}
                        {user.isPhoneVerified && (
                          <span title="Verificado" style={{color: '#10b981', fontSize: '1.1rem'}}>✓</span>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="font-bold" style={{color: 'var(--accent-blue)'}}>{user.totalTrips || user.driverData?.totalTrips || 0}</td>
                  <td style={{color: 'var(--accent-amber)'}}>★ {(user.rating || 5).toFixed(1)}</td>
                  <td className="text-secondary text-sm">
                    {user.createdAt?.toDate ? new Date(user.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td style={{whiteSpace: 'nowrap'}}>
                    <button
                      onClick={() => openSupportChatAndGo(navigate, user.id, user.isDriver ? 'driver' : 'passenger')}
                      style={{
                        background: 'rgba(6, 182, 212, 0.1)',
                        color: 'var(--accent-cyan)',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        marginRight: '6px'
                      }}>
                      Abrir chat
                    </button>
                    <button 
                      onClick={() => handleDelete(user.id)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.8rem'
                      }}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p>No hay usuarios registrados</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Users;
